// Timezone guard for the logged-in CRM (the login page does not render it).
// All CRM times are US Pacific, but date pickers follow the PC clock, so dates entered from
// another timezone can be saved wrong.
// - PC on US Pacific time (same UTC offset right now): nothing.
// - ADMIN on another timezone: red banner that stays at the top of the page while scrolling.
// - Any other role: a modal that cannot be closed until the PC is switched to Pacific time.
// Decided during the first render (no flash of the app), then rechecked every 30 s and when the
// window regains focus, so it goes away by itself once the PC is on Pacific time.
import { useEffect, useState } from 'react';
import { Alert, Button, Modal } from 'antd';
import { formatGapFromPT, pcMinutesAheadOfPT } from '../../utils/usTime';

const CHECK_EVERY_MS = 30 * 1000;

const readState = () => {
  let zone = '';
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    zone = '';
  }
  let isAdmin = false;
  try {
    isAdmin = JSON.parse(localStorage.getItem('userData'))?.role === 'ADMIN';
  } catch {
    isAdmin = false;
  }
  return { zone, isAdmin, minutesAhead: pcMinutesAheadOfPT() };
};

const sameState = (a, b) => a.zone === b.zone && a.isAdmin === b.isAdmin && a.minutesAhead === b.minutesAhead;

const TimezoneBanner = () => {
  const [state, setState] = useState(readState);
  const [fadedIn, setFadedIn] = useState(false);
  const offPacific = state.minutesAhead !== 0;

  useEffect(() => {
    const check = () => {
      const next = readState();
      setState((prev) => (sameState(prev, next) ? prev : next));
    };
    const timer = setInterval(check, CHECK_EVERY_MS);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', check);
    };
  }, []);

  // Banner fade-in: painted once at opacity 0, then the CSS transition takes it to 1.
  useEffect(() => {
    if (!offPacific) return undefined;
    const frame = requestAnimationFrame(() => setFadedIn(true));
    return () => {
      cancelAnimationFrame(frame);
      setFadedIn(false);
    };
  }, [offPacific]);

  if (!offPacific) return null;

  const pcText = `Your computer is set to ${state.zone || 'another timezone'} (${formatGapFromPT(state.minutesAhead)}).`;

  if (state.isAdmin) {
    return (
      <div
        style={{
          position: 'sticky', // inside the scrolling <Content>, so it stays just below the header
          top: 0,
          zIndex: 10, // above page content, below antd dropdowns and modals
          paddingBottom: 16,
          background: '#fff',
          boxShadow: '0 -24px 0 #fff', // covers the content padding above it while stuck
          opacity: fadedIn ? 1 : 0,
          transition: 'opacity 200ms ease-in',
        }}
      >
        <Alert
          type="error"
          showIcon
          message={`${pcText} All CRM times are US Pacific. Please change your computer timezone to Pacific before entering any dates.`}
        />
      </div>
    );
  }

  return (
    <Modal
      open
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      zIndex={2000}
      styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.65)' } }}
      title="Please switch your computer to US Pacific time"
      footer={
        <Button type="primary" onClick={() => window.location.reload()}>
          I changed it – Reload
        </Button>
      }
    >
      {`${pcText} This CRM runs on US Pacific time, so dates can be saved wrong. Change your Windows timezone to 'Pacific Time (US & Canada)': Settings → Time & language → Date & time → Time zone.`}
    </Modal>
  );
};

export default TimezoneBanner;
